import _ from "lodash";
import { postPlayChanges } from "./postPlay";
import { v4 as uuidv4 } from "uuid";
import { isDisplayable } from "util/misc";
import { getDataSportCode, transformGameStatusIfDateAdded } from "games/utils";
import { setNullIfEmptyString } from "games/model/actions";

const transformNotFootballPlaysBlob = (formData) => {
  const { sportCode, gameStatus, gameDateString } = formData;
  const dataSportCode = getDataSportCode(sportCode);

  const sidePlaysBlob = (side) => {
    const formPlayers = _.get(formData, [side, "formPlayers"], []);

    const playerStats = formPlayers.filter((stat) => {
      return _.size(stat) > 2;
    });

    const teamStats = _.get(formData, [side, dataSportCode], {});
    if (teamStats) {
      delete teamStats.__typename;
    }

    return {
      teamStats,
      playerStats
    };
  };

  const playsBlob = {
    summary: [],
    home: sidePlaysBlob("home"),
    away: sidePlaysBlob("away"),
    gameStatus: transformGameStatusIfDateAdded(gameStatus, gameDateString)
  };

  return JSON.stringify(playsBlob);
};

/*
 * @param {object} stats - stats form current state
 * @param {string} side - "home" or "away"
 */
const transformStats = (stats, side) => {
  const playerStats = _.get(stats, [side, "playerStats"], []);
  const teamStats = _.get(stats, [side, "teamStats"], {});
  const transformedPlayerStats = playerStats
    .filter((stat) => _.size(stat) > 2)
    .map((stat) => setNullIfEmptyString(stat));

  return {
    teamStats: setNullIfEmptyString(teamStats),
    playerStats: transformedPlayerStats
  };
};

const transformFormToData = ({ plays, stats, gameStatus }) => {
  const transformedHomeStats = transformStats(stats, "home");
  const transformedAwayStats = transformStats(stats, "away");
  // remove ids from plays
  const transformedPlays = plays.map((p) => {
    return _.omit(p, ["id", "includesFumble", "isGoalDistanceToGo"]);
  });
  const data = {
    summary: transformedPlays,
    home: transformedHomeStats,
    away: transformedAwayStats,
    gameStatus
  };

  return JSON.stringify(data);
};

const getInitialPlayerStats = (side, sideSlug, game) => {
  const playerStats = _.get(side, "playerStats", []);
  const players = _.get(game, [sideSlug, "season", "players"], []);
  const output = players.map((player) => {
    const pStats = playerStats.find(
      (playerStat) => playerStat.id === player.id
    );
    const stats = {
      ...pStats,
      lastName: _.get(player, "person.lastName"),
      jerseyNumber: _.get(player, "jerseyNumber")
    };
    return pStats
      ? stats
      : {
          id: player.id,
          name: _.get(player, "person.name"),
          firstName: _.get(player, "person.firstName"),
          lastName: _.get(player, "person.lastName"),
          jerseyNumber: _.get(player, "jerseyNumber")
        };
  });
  return output;
};

const transformDataToForm = (game) => {
  const playsBlob = _.get(game, "playsBlob", null);
  if (!playsBlob) return [];
  const transformedPlaysBlob = !playsBlob ? [] : JSON.parse(playsBlob);
  const { home = {}, away = {} } = transformedPlaysBlob;
  // add id to each play
  const transformedPlays = _.get(transformedPlaysBlob, "summary", []).map(
    (p) => {
      const hasFumble = _.has(p, "fumble");
      const isFumble = Boolean(_.get(p, "fumble"));
      const isGoalDistanceToGo = _.get(p, "distanceToGo") === 100;
      p.id = uuidv4();
      if (hasFumble) {
        p.includesFumble = isFumble;
      }
      if (isGoalDistanceToGo) {
        p.isGoalDistanceToGo = true;
      }
      return p;
    }
  );

  return {
    plays: transformedPlays,
    gameStatus: _.get(playsBlob, "gameStatus"),
    stats: {
      home: {
        playerStats: getInitialPlayerStats(home, "home", game),
        teamStats: _.get(home, "teamStats", {})
      },
      away: {
        playerStats: getInitialPlayerStats(away, "away", game),
        teamStats: _.get(away, "teamStats", {})
      }
    }
  };
};

/*
 * Clean up play and calculate values after it is edited by the form.
 *
 * @param {object} play - most recent state of play from form
 * @param {object} previousPlay - play that is 1 before (in order, not time) current play
 * @param {object} game - game from current state
 */
const transformPlay = (play, previousPlay, game) => {
  const badKeys = Object.keys(play).filter((key) => !isDisplayable(play[key]));
  const transformedPlay = _.omit(play, badKeys);
  const changes = postPlayChanges(transformedPlay, previousPlay, game);

  return {
    ...transformedPlay,
    ...changes
  };
};

/*
 * Used when editing or deleting a play
 * If "points gained" or "quarter" are changed, this will update all plays
 * after the edited / deleted play
 *
 * @param {object} editedPlay - only used when editing (not deleting) a play
 * @param {number} playIndex - index of edited / deleted play
 * @param {pointsDiff} object
 * @param {array} plays - plays from current state
 */
const transformPlays = (editedPlay, playIndex, pointsDiff, plays) => {
  const uneditedPlay = _.get(plays, playIndex);
  const playWasDeleted = !editedPlay;
  const endOfQuarterWasDeleted =
    playWasDeleted && uneditedPlay.playType === "endOfQuarter";
  return plays.reduce((acc, item, i, arr) => {
    // remove deleted play from plays array
    if (i === playIndex && playWasDeleted) {
      return acc;
      // update edited play in plays array
    } else if (i === playIndex) {
      acc.push(editedPlay);
      return acc;
      // update play if after play with edited points gained
    } else if (i > playIndex && pointsDiff) {
      const homeTotalPoints = _.get(item, "homeTotalPoints") || 0;
      const awayTotalPoints = _.get(item, "awayTotalPoints") || 0;
      item.homeTotalPoints = pointsDiff.home
        ? homeTotalPoints + pointsDiff.home
        : homeTotalPoints;
      item.awayTotalPoints = pointsDiff.away
        ? awayTotalPoints + pointsDiff.away
        : awayTotalPoints;
      item.quarter = endOfQuarterWasDeleted ? item.quarter - 1 : item.quarter;
      acc.push(item);
      return acc;
      // if play is before edited play, keep in plays array unchanged
    } else {
      acc.push(item);
      return acc;
    }
  }, []);
};

export {
  transformFormToData,
  transformDataToForm,
  transformPlays,
  transformPlay,
  transformNotFootballPlaysBlob,
  transformStats
};
